import { useEffect, useMemo, useRef, useState } from "react"

import {
  Alert,
  AlertDescription,
  Button,
  SearchInput,
  Table,
  Tooltip,
  type ColumnDef,
} from "@coval/ui"
import { Pencil, Tags, Trash2 } from "lucide-react"

import { EmptyFilteredState, EmptyStateCTA } from "@/components/EmptyStateCTA"
import { NewResourceButton } from "@/components/ResourceActionButton"
import { useDebounce } from "@/hooks/useDebounce"
import type { TagResource } from "@/types_api/backend/transport/list_organization_tags_response"

import { TagCatalogForm } from "../components/TagCatalogForm"
import { TagChip } from "../components/TagChip"
import { MAX_TAG_LENGTH } from "../constants/tag-constraints"
import { DeleteTagDialogRoute } from "./DeleteTagDialogRoute"
import { useManageTagCatalog } from "./useManageTagCatalog"

const usageLabels = {
  runs: "Runs",
  agents: "Agents",
  test_sets: "Test sets",
  personas: "Personas",
  metrics: "Metrics",
  run_templates: "Run templates",
} as const

type TagDialog =
  | { kind: "create" }
  | { kind: "edit" | "delete"; tag: TagResource }

const columns: ColumnDef<TagResource>[] = [
  {
    id: "name",
    header: "Tag",
    accessorKey: "name",
    minWidth: 180,
    enableResizing: false,
    cell: (tag) => <TagChip tag={tag.name} color={tag.color} />,
  },
  {
    id: "assignment_count",
    header: "Assignments",
    accessorKey: "assignment_count",
    width: 140,
    minWidth: 120,
    flex: false,
    align: "right",
    enableResizing: false,
    cell: (tag) => (
      <Tooltip
        content={
          <div className="space-y-1">
            <p className="font-medium">Live resource usage</p>
            {tag.usage ? (
              Object.entries(usageLabels).map(([key, label]) => (
                <p key={key}>
                  {label}:{" "}
                  {tag.usage?.[
                    key as keyof typeof usageLabels
                  ]?.toLocaleString() ?? "—"}
                </p>
              ))
            ) : (
              <p>Usage unavailable</p>
            )}
            <p>Total assignments include inactive resources.</p>
          </div>
        }
      >
        <span
          role="button"
          tabIndex={0}
          className="tabular-nums"
          aria-label={`Assignments for ${tag.name}`}
        >
          {tag.assignment_count?.toLocaleString() ?? "—"}
        </span>
      </Tooltip>
    ),
  },
]

export function TagManagementRoute() {
  const [search, setSearch] = useState("")
  const debouncedSearch = useDebounce(search.trim(), 300)
  const catalog = useManageTagCatalog(debouncedSearch)
  const [dialog, setDialog] = useState<TagDialog | null>(null)
  const generation = useRef(0)
  useEffect(
    () => () => {
      generation.current += 1
    },
    [],
  )
  const tags = useMemo(
    () =>
      Array.from(
        new Map(
          catalog.query.data?.pages
            .flatMap((page) => page.tags)
            .map((tag) => [tag.id, tag]) ?? [],
        ).values(),
      ),
    [catalog.query.data],
  )
  const hasData = catalog.query.data !== undefined
  const isCatalogReady = hasData && catalog.isReady && !catalog.query.isError
  const canMutate = isCatalogReady && !catalog.isPending
  const catalogError = isCatalogReady
    ? null
    : "The tag catalog is unavailable. Retry loading it before saving or deleting a tag."
  const retryCatalog = () => {
    if (catalog.refreshError) {
      void catalog.refresh()
    } else {
      void catalog.query.refetch()
    }
  }
  const openDialog = (next: TagDialog) => {
    if (canMutate) {
      generation.current += 1
      catalog.clearError()
      setDialog(next)
    }
  }
  const closeDialog = () => {
    if (!catalog.isPending) {
      generation.current += 1
      catalog.clearError()
      setDialog(null)
    }
  }
  const saveTag = async (name: string, color: string | null) => {
    if (!canMutate || !dialog || dialog.kind === "delete") {
      return
    }
    const currentGeneration = generation.current
    const saved =
      dialog.kind === "create"
        ? await catalog.create({ name, ...(color === null ? {} : { color }) })
        : await catalog.update({
            tag_id: dialog.tag.id,
            ...(name !== dialog.tag.name ? { name } : {}),
            ...(color !== (dialog.tag.color ?? null) ? { color } : {}),
          })
    if (saved && generation.current === currentGeneration) {
      setDialog(null)
    }
  }
  const deleteTag = async () => {
    if (!canMutate || dialog?.kind !== "delete") {
      return
    }
    const currentGeneration = generation.current
    const deleted = await catalog.deleteTag(dialog.tag.id)
    if (deleted && generation.current === currentGeneration) {
      setDialog(null)
    }
  }

  return (
    <section className="space-y-6" data-testid="workspace-tag-management">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0 space-y-2">
          <h1 className="text-lg font-semibold">Tags</h1>
          <p className="text-sm text-coval-text-muted">
            Manage shared tags across resources in this workspace.
          </p>
        </div>
        <NewResourceButton
          resourceName="tag"
          disabled={!canMutate}
          onClick={() => openDialog({ kind: "create" })}
          data-testid="create-tag-button"
        />
      </div>
      {catalog.refreshError && !dialog && (
        <Alert variant="danger">
          <AlertDescription>
            {catalog.refreshError}{" "}
            <Button
              variant="secondary"
              disabled={catalog.isPending}
              onClick={retryCatalog}
            >
              Retry refresh
            </Button>
          </AlertDescription>
        </Alert>
      )}
      {catalog.query.isError && !catalog.query.isFetchNextPageError && (
        <Alert variant="danger">
          <AlertDescription>
            {hasData
              ? "Tags could not be refreshed. Showing the last loaded catalog."
              : "Tags could not be loaded."}
            <Button
              variant="secondary"
              onClick={() => {
                void catalog.query.refetch()
              }}
            >
              Retry
            </Button>
          </AlertDescription>
        </Alert>
      )}
      <div className="flex flex-wrap items-center gap-3">
        <SearchInput
          aria-label="Search tags"
          placeholder="Search tags…"
          onClear={() => setSearch("")}
          value={search}
          onChange={(event) =>
            setSearch(
              Array.from(event.target.value).slice(0, MAX_TAG_LENGTH).join(""),
            )
          }
          className="min-w-0 flex-1"
        />
      </div>
      {(hasData || !catalog.query.isError) && (
        <Table
          aria-label="Workspace tags"
          data={tags}
          columns={columns}
          getRowId={(tag) => tag.id}
          isLoading={!hasData}
          framed
          className="h-96"
          rowActions={() => [
            {
              label: "Edit Tag",
              icon: <Pencil />,
              disabled: !canMutate,
              onSelect: (tag) => openDialog({ kind: "edit", tag }),
            },
            {
              label: "Delete Tag",
              icon: <Trash2 />,
              destructive: true,
              disabled: !canMutate,
              onSelect: (tag) => openDialog({ kind: "delete", tag }),
            },
          ]}
          emptyState={
            search.trim() ? (
              <EmptyFilteredState
                title="No tags found"
                resetLabel="Clear search"
                onReset={() => setSearch("")}
              />
            ) : (
              <EmptyStateCTA
                icon={Tags}
                title="Create your first Tag"
                description="Tags organize resources across this workspace."
              >
                <NewResourceButton
                  resourceName="Tag"
                  disabled={!canMutate}
                  onClick={() => openDialog({ kind: "create" })}
                />
              </EmptyStateCTA>
            )
          }
        />
      )}
      {catalog.query.isFetchNextPageError && (
        <Alert variant="danger">
          <AlertDescription>
            More tags could not be loaded. Your loaded tags are still available.
          </AlertDescription>
        </Alert>
      )}
      {(catalog.query.hasNextPage || catalog.query.isFetchNextPageError) && (
        <Button
          variant="secondary"
          disabled={catalog.query.isFetching || catalog.isPending}
          onClick={() => {
            void catalog.query.fetchNextPage()
          }}
        >
          {catalog.query.isFetchingNextPage
            ? "Loading…"
            : catalog.query.isFetchNextPageError
              ? "Retry loading more tags"
              : "Load more tags"}
        </Button>
      )}
      {dialog && dialog.kind !== "delete" && (
        <TagCatalogForm
          key={dialog.kind === "edit" ? dialog.tag.id : "create"}
          tag={dialog.kind === "edit" ? dialog.tag : undefined}
          tags={tags}
          isPending={catalog.isPending}
          isCatalogReady={isCatalogReady}
          catalogError={catalogError}
          onRetryCatalog={retryCatalog}
          error={catalog.error}
          onClose={closeDialog}
          onSave={(name, color) => {
            void saveTag(name, color)
          }}
        />
      )}
      {dialog?.kind === "delete" && (
        <DeleteTagDialogRoute
          tag={dialog.tag}
          isPending={catalog.isPending}
          isCatalogReady={isCatalogReady}
          catalogError={catalogError}
          onRetryCatalog={retryCatalog}
          error={catalog.error}
          onClose={closeDialog}
          onConfirm={() => {
            void deleteTag()
          }}
        />
      )}
    </section>
  )
}
